import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Filter,
  Building2,
  Mail,
  Phone,
  DollarSign,
  Users,
  TrendingUp,
  Calendar,
  ChevronRight,
  MoreHorizontal,
  Edit,
  Eye,
  Trash2,
  Loader2,
  ArrowRight,
  FileText,
  Clock,
  Target,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const STAGES = [
  { id: 'inquiry', label: 'Inquiry', color: 'bg-slate-500' },
  { id: 'qualification', label: 'Qualification', color: 'bg-blue-500' },
  { id: 'proposal', label: 'Proposal', color: 'bg-purple-500' },
  { id: 'negotiation', label: 'Negotiation', color: 'bg-amber-500' },
  { id: 'loi', label: 'LOI', color: 'bg-orange-500' },
  { id: 'lease', label: 'Lease', color: 'bg-emerald-500' },
  { id: 'won', label: 'Won', color: 'bg-green-500' },
  { id: 'lost', label: 'Lost', color: 'bg-red-500' },
];

const CATEGORIES = [
  'Fashion - Apparel',
  'Fashion - Footwear',
  'Fashion - Accessories',
  'Electronics',
  'Food & Beverage',
  'Entertainment',
  'Beauty & Personal Care',
  'Jewelry & Watches',
  'Home & Living',
  'Sports & Fitness',
  'Services',
  'Hypermarket',
  'Other',
];

export default function LeasingCRMPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [pipelineSummary, setPipelineSummary] = useState({});
  const [malls, setMalls] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStage, setFilterStage] = useState('all');
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [viewLeadOpen, setViewLeadOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [leadForm, setLeadForm] = useState({
    brand_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    category: '',
    required_area_sqft: '',
    preferred_floor: '',
    preferred_mall_id: '',
    notes: '',
    source: 'direct',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [leadsRes, mallsRes, summaryRes] = await Promise.all([
        api.get('/leasing/'),
        api.get('/malls/'),
        api.get('/leasing/pipeline/summary'),
      ]);
      setLeads(leadsRes.data);
      setMalls(mallsRes.data);
      setPipelineSummary(summaryRes.data?.stages || {});
    } catch (error) {
      console.error('Error fetching leasing data:', error);
      toast.error('Failed to load leasing data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLead = async () => {
    if (!leadForm.brand_name || !leadForm.contact_email) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      setSubmitting(true);
      await api.post('/leasing/', {
        ...leadForm,
        required_area_sqft: parseFloat(leadForm.required_area_sqft) || 0,
        preferred_floor: leadForm.preferred_floor ? parseInt(leadForm.preferred_floor) : null,
      });
      toast.success('Lead created successfully');
      setNewLeadOpen(false);
      setLeadForm({
        brand_name: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        category: '',
        required_area_sqft: '',
        preferred_floor: '',
        preferred_mall_id: '',
        notes: '',
        source: 'direct',
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create lead');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStage = async (leadId, newStage) => {
    try {
      await api.put(`/leasing/${leadId}`, { stage: newStage });
      toast.success(`Lead moved to ${newStage}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  const getLeadsByStage = (stageId) => {
    return leads.filter(lead => {
      const matchesStage = lead.stage === stageId;
      const matchesSearch = !searchQuery || 
        lead.brand_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lead.contact_name?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStage && matchesSearch;
    });
  };

  const totalPipelineValue = Object.values(pipelineSummary).reduce((sum, stage) => 
    sum + (stage?.value || 0), 0
  );

  const totalLeads = Object.values(pipelineSummary).reduce((sum, stage) => 
    sum + (stage?.count || 0), 0
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="leasing-crm-loading">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-primary animate-spin" />
          <p className="text-muted-foreground">Loading leasing CRM...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leasing-crm">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Leasing CRM</h1>
          <p className="text-muted-foreground">
            Manage leads and track your leasing pipeline
          </p>
        </div>
        <Dialog open={newLeadOpen} onOpenChange={setNewLeadOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-lead-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New Lead</DialogTitle>
              <DialogDescription>
                Enter the brand details to create a new leasing lead
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Brand Name *</Label>
                <Input
                  placeholder="e.g., Zara, Nike"
                  value={leadForm.brand_name}
                  onChange={(e) => setLeadForm(f => ({ ...f, brand_name: e.target.value }))}
                  data-testid="lead-brand-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={leadForm.category} onValueChange={(v) => setLeadForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="lead-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Contact Name *</Label>
                <Input
                  placeholder="Primary contact"
                  value={leadForm.contact_name}
                  onChange={(e) => setLeadForm(f => ({ ...f, contact_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Email *</Label>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={leadForm.contact_email}
                  onChange={(e) => setLeadForm(f => ({ ...f, contact_email: e.target.value }))}
                  data-testid="lead-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Phone</Label>
                <Input
                  placeholder="+91 9876543210"
                  value={leadForm.contact_phone}
                  onChange={(e) => setLeadForm(f => ({ ...f, contact_phone: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Required Area (sq ft)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 2500"
                  value={leadForm.required_area_sqft}
                  onChange={(e) => setLeadForm(f => ({ ...f, required_area_sqft: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Preferred Mall</Label>
                <Select value={leadForm.preferred_mall_id} onValueChange={(v) => setLeadForm(f => ({ ...f, preferred_mall_id: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select mall" />
                  </SelectTrigger>
                  <SelectContent>
                    {malls.map(mall => (
                      <SelectItem key={mall.id} value={mall.id}>{mall.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Preferred Floor</Label>
                <Select value={leadForm.preferred_floor} onValueChange={(v) => setLeadForm(f => ({ ...f, preferred_floor: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select floor" />
                  </SelectTrigger>
                  <SelectContent>
                    {[0, 1, 2, 3, 4, 5].map(floor => (
                      <SelectItem key={floor} value={floor.toString()}>
                        {floor === 0 ? 'Ground Floor' : `Floor ${floor}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Source</Label>
                <Select value={leadForm.source} onValueChange={(v) => setLeadForm(f => ({ ...f, source: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direct">Direct</SelectItem>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="broker">Broker</SelectItem>
                    <SelectItem value="cold_call">Cold Call</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Notes</Label>
                <Textarea
                  placeholder="Additional notes about the lead..."
                  rows={3}
                  value={leadForm.notes}
                  onChange={(e) => setLeadForm(f => ({ ...f, notes: e.target.value }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewLeadOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateLead} disabled={submitting} data-testid="submit-lead-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Lead
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Leads</p>
                <p className="text-2xl font-bold mt-1">{totalLeads}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pipeline Value</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(totalPipelineValue)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In Negotiation</p>
                <p className="text-2xl font-bold mt-1">{pipelineSummary?.negotiation?.count || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Target className="w-6 h-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Won This Month</p>
                <p className="text-2xl font-bold mt-1">{pipelineSummary?.won?.count || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search leads by brand or contact..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-leads-input"
          />
        </div>
        <Select value={filterStage} onValueChange={setFilterStage}>
          <SelectTrigger className="w-[180px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {STAGES.map(stage => (
              <SelectItem key={stage.id} value={stage.id}>{stage.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Kanban Board */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max" data-testid="kanban-board">
          {STAGES.filter(s => s.id !== 'won' && s.id !== 'lost').map(stage => (
            <div key={stage.id} className="w-72 flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${stage.color}`} />
                  <h3 className="font-medium text-sm">{stage.label}</h3>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {getLeadsByStage(stage.id).length}
                </Badge>
              </div>
              
              <ScrollArea className="h-[calc(100vh-420px)] pr-2">
                <div className="space-y-3">
                  {getLeadsByStage(stage.id).map(lead => (
                    <Card 
                      key={lead.id} 
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => {
                        setSelectedLead(lead);
                        setViewLeadOpen(true);
                      }}
                      data-testid={`lead-card-${lead.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-sm line-clamp-1">{lead.brand_name}</h4>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-6 w-6">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                setSelectedLead(lead);
                                setViewLeadOpen(true);
                              }}>
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              {stage.id !== 'inquiry' && (
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation();
                                  const currentIndex = STAGES.findIndex(s => s.id === stage.id);
                                  if (currentIndex > 0) {
                                    handleUpdateStage(lead.id, STAGES[currentIndex - 1].id);
                                  }
                                }}>
                                  <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
                                  Move Back
                                </DropdownMenuItem>
                              )}
                              {stage.id !== 'lease' && (
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation();
                                  const currentIndex = STAGES.findIndex(s => s.id === stage.id);
                                  if (currentIndex < STAGES.length - 3) {
                                    handleUpdateStage(lead.id, STAGES[currentIndex + 1].id);
                                  }
                                }}>
                                  <ArrowRight className="w-4 h-4 mr-2" />
                                  Move Forward
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem 
                                className="text-green-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateStage(lead.id, 'won');
                                }}
                              >
                                <TrendingUp className="w-4 h-4 mr-2" />
                                Mark as Won
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateStage(lead.id, 'lost');
                                }}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Mark as Lost
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        
                        <Badge variant="outline" className="text-xs mb-2">
                          {lead.category || 'Uncategorized'}
                        </Badge>
                        
                        <div className="space-y-1 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            <span className="line-clamp-1">{lead.contact_name}</span>
                          </div>
                          {lead.required_area_sqft > 0 && (
                            <div className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              <span>{lead.required_area_sqft.toLocaleString()} sq ft</span>
                            </div>
                          )}
                          {lead.deal_value > 0 && (
                            <div className="flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              <span>{formatCurrency(lead.deal_value)}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between mt-3 pt-2 border-t">
                          <span className="text-xs text-muted-foreground">{lead.source}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(lead.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {getLeadsByStage(stage.id).length === 0 && (
                    <div className="p-4 text-center text-sm text-muted-foreground bg-muted/30 rounded-lg">
                      No leads in this stage
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          ))}
        </div>
      </div>

      {/* Won/Lost Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-green-500/20 bg-green-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-green-600 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Won Deals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold">{pipelineSummary?.won?.count || 0}</span>
              <span className="text-lg font-medium text-green-600">
                {formatCurrency(pipelineSummary?.won?.value || 0)}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-500/20 bg-red-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-red-600 flex items-center gap-2">
              <Trash2 className="w-4 h-4" />
              Lost Deals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold">{pipelineSummary?.lost?.count || 0}</span>
              <span className="text-lg font-medium text-red-600">
                {formatCurrency(pipelineSummary?.lost?.value || 0)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lead Detail Dialog */}
      <Dialog open={viewLeadOpen} onOpenChange={setViewLeadOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedLead?.brand_name}</DialogTitle>
            <DialogDescription>Lead details and activity history</DialogDescription>
          </DialogHeader>
          
          {selectedLead && (
            <div className="space-y-6 py-4">
              <div className="flex items-center gap-4">
                <Badge className={STAGES.find(s => s.id === selectedLead.stage)?.color}>
                  {STAGES.find(s => s.id === selectedLead.stage)?.label}
                </Badge>
                <Badge variant="outline">{selectedLead.category}</Badge>
                <Badge variant="outline">{selectedLead.source}</Badge>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Contact Name</p>
                  <p className="font-medium">{selectedLead.contact_name}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{selectedLead.contact_email}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{selectedLead.contact_phone || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Required Area</p>
                  <p className="font-medium">{selectedLead.required_area_sqft?.toLocaleString() || 'N/A'} sq ft</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Deal Value</p>
                  <p className="font-medium">{formatCurrency(selectedLead.deal_value)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Probability</p>
                  <p className="font-medium">{((selectedLead.probability || 0) * 100).toFixed(0)}%</p>
                </div>
              </div>

              {selectedLead.notes && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p className="text-sm bg-muted/50 p-3 rounded-lg">{selectedLead.notes}</p>
                </div>
              )}

              {/* Activity Timeline */}
              {selectedLead.activities?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-medium text-sm">Activity Timeline</h4>
                  <div className="space-y-2">
                    {selectedLead.activities.map((activity, idx) => (
                      <div key={idx} className="flex gap-3 text-sm">
                        <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                          <Clock className="w-3 h-3" />
                        </div>
                        <div>
                          <p>{activity.message}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(activity.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewLeadOpen(false)}>Close</Button>
            <Select 
              value={selectedLead?.stage}
              onValueChange={(v) => {
                handleUpdateStage(selectedLead.id, v);
                setViewLeadOpen(false);
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Move to stage" />
              </SelectTrigger>
              <SelectContent>
                {STAGES.map(stage => (
                  <SelectItem key={stage.id} value={stage.id}>{stage.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
